import { type Component, createResource, For, Show } from 'solid-js'
import Badge from '~/components/Badge'
import Button from '~/components/Button'
import Drawer from '~/components/Drawer'
import Icon from '~/components/Icon'
import Skeleton from '~/components/Skeleton'
import { usePermissions } from '~/hooks/usePermissions'
import { type Path, useNavigate } from '~/router'
import { formService } from '~/services/form.service'
import { leadService } from '~/services/lead.service'
import { inventoryService } from '~/services/product.service'
import { quoteService } from '~/services/quote.service'
import type { AlertItemType } from '~/types/Notifications'

import { P } from '~/utils/permissions'

interface NotificationsDrawerProps {
  isOpen: boolean
  onClose: () => void
}

/**
 * Clases completas y literales: Tailwind no genera las que se arman
 * concatenando (`bg-${color}-soft` no existiría en el CSS final).
 */
const ALERT_STYLES: Record<AlertItemType['color'], { box: string; icon: string }> = {
  info: { box: 'bg-info-soft', icon: 'text-info' },
  warning: { box: 'bg-warning-soft', icon: 'text-warning' },
  danger: { box: 'bg-danger-soft', icon: 'text-danger' },
  success: { box: 'bg-success-soft', icon: 'text-success' }
}

/**
 * Avisos de lo que hay pendiente. No existe un módulo de notificaciones: esto
 * mira lo que el sistema ya sabe —prospectos sin atender, cotizaciones sin
 * respuesta, existencias bajo mínimos y respuestas de formularios— y lleva a la
 * pantalla donde se resuelve cada cosa.
 *
 * Cada consulta va por separado y a prueba de fallos: que una pantalla no esté
 * permitida o que su petición falle no debe vaciar el panel entero.
 */
const NotificationsDrawer: Component<NotificationsDrawerProps> = props => {
  const navigate = useNavigate()
  const { can } = usePermissions()

  const [alerts, { refetch }] = createResource(
    () => ({ open: props.isOpen }),
    async ({ open }): Promise<AlertItemType[]> => {
      // Sólo se consulta con el panel abierto: es información de un vistazo.
      if (!open) return []

      const seguro = async <T,>(permitido: boolean, fn: () => Promise<T>, fallback: T): Promise<T> => {
        if (!permitido) return fallback
        try {
          return await fn()
        } catch {
          return fallback
        }
      }

      const [leads, quotes, solicitudes, stock, forms] = await Promise.all([
        seguro(can(P.leads.read), () => leadService.list({ limit: 100 }), { items: [], meta: { total: 0, page: 1, perPage: 0, totalPages: 1 } }),
        seguro(can(P.quotes.read), () => quoteService.list({ limit: 100, status: 'sent' }), { items: [], meta: { total: 0, page: 1, perPage: 0, totalPages: 1 } }),
        // Borradores, que es como entran las solicitudes del catálogo público.
        seguro(can(P.quotes.read), () => quoteService.list({ limit: 100, status: 'draft' }), { items: [], meta: { total: 0, page: 1, perPage: 0, totalPages: 1 } }),
        seguro(can(P.inventory.read), () => inventoryService.list(true), { items: [], meta: { total: 0, lowStock: 0, sinExistencias: 0 } }),
        seguro(can(P.forms.read), () => formService.list({ limit: 100 }), { items: [], meta: { total: 0, page: 1, perPage: 0, totalPages: 1 } })
      ])

      const sinAtender = leads.items.filter(l => !l.status).length
      const respuestas = forms.items.reduce((total, form) => total + (form.responsesCount ?? 0), 0)
      const delCatalogo = solicitudes.items.filter(q => q.meta?.origin === 'catalogo-publico').length

      const items: AlertItemType[] = [
        {
          id: 'solicitudes',
          count: delCatalogo,
          icon: 'material-symbols-light:shopping-bag-outline',
          color: 'success',
          title: delCatalogo === 1 ? 'Una solicitud del catálogo' : `${delCatalogo} solicitudes del catálogo`,
          detail: 'Las pidió un cliente desde la ficha pública y están en borrador',
          href: '/admin/cotizaciones'
        },
        {
          id: 'prospectos',
          count: sinAtender,
          icon: 'material-symbols-light:person-add-outline',
          color: 'info',
          title: sinAtender === 1 ? 'Un prospecto sin atender' : `${sinAtender} prospectos sin atender`,
          detail: 'Llegaron por el asesor de la web',
          href: '/admin/prospectos'
        },
        {
          id: 'cotizaciones',
          count: quotes.items.length,
          icon: 'material-symbols-light:description-outline',
          color: 'warning',
          title: quotes.items.length === 1 ? 'Una cotización sin respuesta' : `${quotes.items.length} cotizaciones sin respuesta`,
          detail: 'Enviadas y todavía sin aprobar ni rechazar',
          href: '/admin/cotizaciones'
        },
        {
          id: 'inventario',
          count: stock.meta.lowStock,
          icon: 'material-symbols-light:warning-outline',
          color: 'danger',
          title: stock.meta.lowStock === 1 ? 'Un producto bajo mínimos' : `${stock.meta.lowStock} productos bajo mínimos`,
          detail: 'Conviene reponer antes de quedarte sin existencias',
          href: '/admin/inventario'
        },
        {
          id: 'formularios',
          count: respuestas,
          icon: 'material-symbols-light:assignment-outline',
          color: 'success',
          title: respuestas === 1 ? 'Una respuesta de formulario' : `${respuestas} respuestas de formulario`,
          detail: 'Recibidas por los formularios publicados',
          href: '/admin/formularios'
        }
      ]

      return items.filter(item => item.count > 0)
    }
  )

  const total = () => (alerts() ?? []).reduce((sum, a) => sum + a.count, 0)

  const abrir = (href: string) => {
    props.onClose()
    navigate(href as Path)
  }

  const headerContent = (
    <div class="flex items-center justify-between w-full">
      <div class="flex items-center gap-2">
        <h2 class="text-lg font-semibold text-foreground">Avisos</h2>
        <Show when={total() > 0}>
          <Badge color="primary" size="sm">
            {total()}
          </Badge>
        </Show>
      </div>
      <Button variant="text" color="default" class="p-1.5!" icon="material-symbols-light:refresh" tooltip="Actualizar" onClick={() => refetch()} />
    </div>
  )

  return (
    <Drawer isOpen={props.isOpen} onClose={props.onClose} headerContent={headerContent}>
      <div class="space-y-3">
        <Show
          when={!alerts.loading}
          fallback={
            <div class="space-y-3">
              <Skeleton height="4.5rem" />
              <Skeleton height="4.5rem" />
              <Skeleton height="4.5rem" />
            </div>
          }
        >
          <Show
            when={(alerts() ?? []).length > 0}
            fallback={
              <div class="flex flex-col items-center justify-center gap-3 py-16 text-center">
                <Icon icon="material-symbols-light:check-circle-outline" size={48} class="text-success" />
                <div>
                  <p class="font-medium text-foreground">Todo al día</p>
                  <p class="text-sm text-foreground-subtle">No hay nada pendiente por revisar.</p>
                </div>
              </div>
            }
          >
            <For each={alerts()}>
              {alert => (
                <button
                  type="button"
                  onClick={() => abrir(alert.href)}
                  class="w-full flex items-start gap-3 rounded-xl border border-border p-3 text-left transition-colors hover:border-primary hover:bg-surface-hover"
                >
                  <span class={`shrink-0 rounded-lg p-2 ${ALERT_STYLES[alert.color].box}`}>
                    <Icon icon={alert.icon} size={22} class={`${ALERT_STYLES[alert.color].icon} block`} />
                  </span>
                  <span class="min-w-0 flex-1">
                    <span class="block font-medium text-foreground">{alert.title}</span>
                    <span class="block text-xs text-foreground-subtle">{alert.detail}</span>
                  </span>
                  <Icon icon="material-symbols-light:chevron-right" size={20} class="shrink-0 text-foreground-subtle" />
                </button>
              )}
            </For>

            <p class="pt-2 text-xs text-foreground-subtle">Se calculan al abrir este panel a partir de lo que hay ahora mismo en el sistema.</p>
          </Show>
        </Show>
      </div>
    </Drawer>
  )
}

export default NotificationsDrawer
